// Redux slice for points and leaderboard state management

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  LeaderboardData,
  PointsData,
  PointActivity,
  UpdatePointsRequest,
  GetPointsLeaderboardRequest,
  LeaderboardFilters,
  PointsLeaderboardState
} from '../../types/points';
import { PointsAPI } from '../../lib/PointsAndLeaderboard';

// Async thunks
export const updatePoints = createAsyncThunk(
  'points/updatePoints',
  async (request: UpdatePointsRequest, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.updatePoints(request);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to update points');
      }
      return {
        updated_points: response.updated_points!,
        activity_recorded: response.activity_recorded!
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

export const fetchPointsLeaderboard = createAsyncThunk(
  'points/fetchLeaderboard',
  async (request: GetPointsLeaderboardRequest, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.getPointsLeaderboard(request);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to fetch leaderboard');
      }
      return response.data!;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

export const awardPointsForExpense = createAsyncThunk(
  'points/awardExpensePoints',
  async (params: {
    couple_id: string;
    partner: 'partner1' | 'partner2';
    expense_id: string;
    expense_amount: number;
    category_name?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.awardPointsForExpense(
        params.couple_id,
        params.partner,
        params.expense_id,
        params.expense_amount,
        params.category_name
      );
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to award expense points');
      }
      return {
        updated_points: response.updated_points!,
        activity_recorded: response.activity_recorded!
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

export const awardPointsForChallenge = createAsyncThunk(
  'points/awardChallengePoints',
  async (params: {
    couple_id: string;
    partner: 'partner1' | 'partner2';
    challenge_id: string;
    challenge_name: string;
    points_reward: number;
  }, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.awardPointsForChallenge(
        params.couple_id,
        params.partner,
        params.challenge_id,
        params.challenge_name,
        params.points_reward
      );
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to award challenge points');
      }
      return {
        updated_points: response.updated_points!,
        activity_recorded: response.activity_recorded!
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

export const awardPointsForAchievement = createAsyncThunk(
  'points/awardAchievementPoints',
  async (params: {
    couple_id: string;
    partner: 'partner1' | 'partner2';
    achievement_id: string;
    achievement_name: string;
    points_awarded: number;
  }, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.awardPointsForAchievement(
        params.couple_id,
        params.partner,
        params.achievement_id,
        params.achievement_name,
        params.points_awarded
      );
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to award achievement points');
      }
      return {
        updated_points: response.updated_points!,
        activity_recorded: response.activity_recorded!
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

export const refreshLeaderboard = createAsyncThunk(
  'points/refreshLeaderboard',
  async (couple_id: string, { rejectWithValue }) => {
    try {
      const response = await PointsAPI.getFullLeaderboard(couple_id);
      if (!response.success) {
        return rejectWithValue(response.error || 'Failed to refresh leaderboard');
      }
      return response.data!;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

// Initial state
const initialState: PointsLeaderboardState = {
  data: null,
  isLoading: false,
  error: null,
  filters: {},
  selectedView: 'overview',
  showOnlyCurrentMonth: true
};

const pointsSlice = createSlice({
  name: 'points',
  initialState,
  reducers: {
    // Clear errors
    clearError: (state) => {
      state.error = null;
    },
    
    // Update filters
    setFilters: (state, action: PayloadAction<Partial<LeaderboardFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
    },
    
    // Update selected view
    setSelectedView: (state, action: PayloadAction<'overview' | 'monthly' | 'activities' | 'comparison'>) => {
      state.selectedView = action.payload;
    },
    
    // Toggle month filter
    setShowOnlyCurrentMonth: (state, action: PayloadAction<boolean>) => {
      state.showOnlyCurrentMonth = action.payload;
    },
    
    // Add new activity to recent activities (for real-time updates)
    addRecentActivity: (state, action: PayloadAction<PointActivity>) => {
      if (state.data) {
        state.data.recent_activities.unshift(action.payload);
        // Keep only last 50 activities
        if (state.data.recent_activities.length > 50) {
          state.data.recent_activities = state.data.recent_activities.slice(0, 50);
        }
      }
    },
    
    // Update current month points (for real-time updates)
    updateCurrentMonthPoints: (state, action: PayloadAction<{
      partner1_points: number;
      partner2_points: number;
    }>) => {
      if (state.data) {
        state.data.current_month.partner1_points = action.payload.partner1_points;
        state.data.current_month.partner2_points = action.payload.partner2_points;
        state.data.current_month.total_points = action.payload.partner1_points + action.payload.partner2_points;
        
        // Recalculate partner comparison
        const totalPoints = state.data.current_month.total_points;
        const pointsDifference = Math.abs(action.payload.partner1_points - action.payload.partner2_points);
        const isPartner1Leading = action.payload.partner1_points > action.payload.partner2_points;
        const isTie = action.payload.partner1_points === action.payload.partner2_points;
        
        state.data.partner_comparison = {
          ...state.data.partner_comparison,
          leading_partner: isTie ? state.data.partner_comparison.leading_partner : 
                          isPartner1Leading ? 'partner1' : 'partner2',
          points_difference: pointsDifference,
          percentage_difference: totalPoints > 0 ? (pointsDifference / totalPoints) * 100 : 0,
          is_tie: isTie
        };
      }
    },
    
    // Reset points state
    resetPointsState: (state) => {
      return { ...initialState };
    },
    
    // Set loading state manually (for optimistic updates)
    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Update points
    builder
      .addCase(updatePoints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePoints.fulfilled, (state, action) => {
        state.isLoading = false;
        
        const { updated_points, activity_recorded } = action.payload;
        
        // Update current month points if we have leaderboard data
        if (state.data) {
          state.data.current_month.partner1_points = updated_points.partner1_points;
          state.data.current_month.partner2_points = updated_points.partner2_points;
          state.data.current_month.total_points = updated_points.total_points;
          
          // Add the new activity to recent activities
          state.data.recent_activities.unshift(activity_recorded);
          
          // Keep only last 50 activities
          if (state.data.recent_activities.length > 50) {
            state.data.recent_activities = state.data.recent_activities.slice(0, 50);
          }
          
          // Update partner comparison
          const pointsDifference = Math.abs(updated_points.partner1_points - updated_points.partner2_points);
          const isPartner1Leading = updated_points.partner1_points > updated_points.partner2_points;
          const isTie = updated_points.partner1_points === updated_points.partner2_points;
          
          state.data.partner_comparison = {
            ...state.data.partner_comparison,
            leading_partner: isTie ? state.data.partner_comparison.leading_partner : 
                            isPartner1Leading ? 'partner1' : 'partner2',
            points_difference: pointsDifference,
            percentage_difference: updated_points.total_points > 0 ? 
                                   (pointsDifference / updated_points.total_points) * 100 : 0,
            is_tie: isTie
          };
        }
      })
      .addCase(updatePoints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    
    // Fetch points leaderboard
    builder
      .addCase(fetchPointsLeaderboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPointsLeaderboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchPointsLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    
    // Award points for expense
    builder
      .addCase(awardPointsForExpense.fulfilled, (state, action) => {
        const { updated_points, activity_recorded } = action.payload;
        
        if (state.data) {
          // Update current month points
          state.data.current_month.partner1_points = updated_points.partner1_points;
          state.data.current_month.partner2_points = updated_points.partner2_points;
          state.data.current_month.total_points = updated_points.total_points;
          
          // Add activity to recent activities
          state.data.recent_activities.unshift(activity_recorded);
          if (state.data.recent_activities.length > 50) {
            state.data.recent_activities = state.data.recent_activities.slice(0, 50);
          }
        }
      })
      .addCase(awardPointsForExpense.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    
    // Award points for challenge
    builder
      .addCase(awardPointsForChallenge.fulfilled, (state, action) => {
        const { updated_points, activity_recorded } = action.payload;
        
        if (state.data) {
          state.data.current_month.partner1_points = updated_points.partner1_points;
          state.data.current_month.partner2_points = updated_points.partner2_points;
          state.data.current_month.total_points = updated_points.total_points;
          
          state.data.recent_activities.unshift(activity_recorded);
          if (state.data.recent_activities.length > 50) {
            state.data.recent_activities = state.data.recent_activities.slice(0, 50);
          }
        }
      })
      .addCase(awardPointsForChallenge.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    
    // Award points for achievement
    builder
      .addCase(awardPointsForAchievement.fulfilled, (state, action) => {
        const { updated_points, activity_recorded } = action.payload;
        
        if (state.data) {
          state.data.current_month.partner1_points = updated_points.partner1_points;
          state.data.current_month.partner2_points = updated_points.partner2_points;
          state.data.current_month.total_points = updated_points.total_points;
          
          state.data.recent_activities.unshift(activity_recorded);
          if (state.data.recent_activities.length > 50) {
            state.data.recent_activities = state.data.recent_activities.slice(0, 50);
          }
        }
      })
      .addCase(awardPointsForAchievement.rejected, (state, action) => {
        state.error = action.payload as string;
      });
    
    // Refresh leaderboard
    builder
      .addCase(refreshLeaderboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshLeaderboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(refreshLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setSelectedView,
  setShowOnlyCurrentMonth,
  addRecentActivity,
  updateCurrentMonthPoints,
  resetPointsState,
  setLoadingState,
} = pointsSlice.actions;

// Selectors
export const selectPointsData = (state: { points: PointsLeaderboardState }) => state.points.data;
export const selectPointsLoading = (state: { points: PointsLeaderboardState }) => state.points.isLoading;
export const selectPointsError = (state: { points: PointsLeaderboardState }) => state.points.error;
export const selectPointsFilters = (state: { points: PointsLeaderboardState }) => state.points.filters;
export const selectSelectedView = (state: { points: PointsLeaderboardState }) => state.points.selectedView;
export const selectShowOnlyCurrentMonth = (state: { points: PointsLeaderboardState }) => state.points.showOnlyCurrentMonth;

export const selectCurrentMonthPoints = (state: { points: PointsLeaderboardState }) => 
  state.points.data?.current_month;

export const selectPartnerComparison = (state: { points: PointsLeaderboardState }) => 
  state.points.data?.partner_comparison;

export const selectRecentActivities = (state: { points: PointsLeaderboardState }) => 
  state.points.data?.recent_activities || [];

export const selectYearlyStats = (state: { points: PointsLeaderboardState }) => 
  state.points.data?.yearly_stats;

export default pointsSlice.reducer;